import { QuestionValue } from '@components/dynamic-form/types';
import { QUESTION_TYPE } from '@components/dynamic-form/dynamic-form.validation';
import DateInput from '@components/date-picker/DatePicker';
import MultiSelectInput from '@components/multi-select';
import SelectInput from '@components/select/Select';
import TextInput from '@src/app-components/text-input/TextInput.base';
import YesNoInput from '@components/yes-no-input/YesNoInput';
import { SelectInputOption } from '@src/api/types';

export type Snb = string | number | boolean | null;
export type TableInputRecordId = string | number;
export type TableId = number | string;
export type TableActions = 'add' | 'delete' | 'undo-delete' | 'update';

export type TableInputOnChange = {
  action: TableActions;
  isValid: boolean;
  pageNumber: number;

  colIndex?: number;
  recordId?: TableInputRecordId;
  rowIndex?: number;
  value?: QuestionValue;
};

export type ChangeEvent = {
  target?: {
    value?: QuestionValue;
  };
};

export type TableInputOnChangeData = {
  action: TableActions;

  recordId?: TableInputRecordId;
  colIndex?: number;
  rowIndex?: number;
  newRow?: TableInputRowData;
  value?: QuestionValue;
};

export type TableInputProps = {
  formId: number;
  isReadOnly?: boolean;
  isRequired: boolean;
  labelText: string;
  onChange: (value: TableInputOnChangeData) => void;
  tableId: TableId;
  tooltipClickable?: boolean;
  tooltipText?: string;
};

export type TableInputSelectOption = SelectInputOption & { type?: string };

export type TableInputColumn = {
  columnId: number;
  columnLabel: string;
  columnNumber: number;
  errorText?: string;
  helpText?: string;
  isRequired: boolean;
  options?: TableInputSelectOption[];
  questionTypeId: number;
  width?: number; // pixels
};

export type TableInputCellData = {
  columnId: number;
  recordId: TableInputRecordId;
  rowNumber: number | null;
  typeId: number;
  status?: string;
  value: QuestionValue;
  errorText?: string;
  options?: TableInputSelectOption[];
};

export type TableInputCellValidation = {
  colIndex: number;
  message: string;
};

export type TableInputRowValidator = (
  rowData: TableInputRowData,
  allRows: TableInputRowData[]
) => TableInputCellValidation[];

export type TableInputRowData = TableInputCellData[];

export type TableInputPageData = { rowData: TableInputRowData[] };

export type TableInputDataByPage = {
  [pageNumber: number]: TableInputPageData;
};

export type TableInputQuestionData = {
  currentPage: number;
  columnData: TableInputColumn[];
  itemsPerPage: number;
  pagedData: TableInputDataByPage;
  rowCount: number;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
};

export type TableInputSelectedRecord = {
  recordId: TableInputRecordId;
  rowNum: number;
};

export type TableInputBulkSelectedRecords = { [recordId: string]: number };

export type TableInputUpdate = {
  [recordId in TableInputRecordId]: {
    [cellIndex: number]: QuestionValue;
  };
};

export type TableInputDelete = {
  [recordId in TableInputRecordId]: boolean;
};

export type TableInputData = {
  [tableId: TableId]: TableInputQuestionData;
};

export type TableInputUpdates = {
  [tableId: TableId]: TableInputUpdate;
};

export type TableInputDeletes = {
  [tableId: TableId]: TableInputDelete;
};

export type TableInputDeleteExclusion = {
  [recordId in TableInputRecordId]: boolean;
};

export type TableInputDeleteExclusions = {
  [tableId: TableId]: TableInputDeleteExclusion;
};

// JPA specific types

export type JpaTableInputOnChangeProps =
  | {
      cellIndex?: number;
      columnIndex: number;
      TableInputDataByPage: number;
      rowIndex: number;
    }
  | undefined;

export type JpaTableInputData = TableInputRowData[];

export type JpaFileDownloadData = {
  displayMessage: string;
  documentDisplayName: string;
  documentName: string;
  fileSize: string;
  fileType: string;
  isFullDisplayLocation: boolean;
  isInitialDisplayLocation: boolean;
  jpaDocumentID: number;
  jpaDocumentQuestionIDs: number[];
};

export type JpaTableInputProps = {
  fileDownload: JpaFileDownloadData[];
  formId: number;
  isReadOnly?: boolean;
  isRequired: boolean;
  label: string;
  onChange: (value: unknown) => void;
  questionId: number;
  tooltipClickable?: boolean;
  tooltipText?: string;
};

export type LastUpdatedData = {
  [key: string]: number;
};

export const JpaTableInputMap = {
  [QUESTION_TYPE.date]: DateInput,
  [QUESTION_TYPE.option]: MultiSelectInput,
  [QUESTION_TYPE.number]: TextInput,
  [QUESTION_TYPE.option]: SelectInput,
  [QUESTION_TYPE.shortAnswer]: TextInput,
  [QUESTION_TYPE.yesNo]: YesNoInput,
};

export type ChangeEventLike = {
  target?: {
    value?: QuestionValue;
  };
};

export type JpaTableInputCellData = {
  answerId: number;
  isDeleted: boolean;
  questionId: number;
  recordId: string | null;
  rowNumber: number | null;
  typeId: number;
  value: QuestionValue;
};

export type JpaTableInputRowData = JpaTableInputCellData[];

export type JpaTableTableData = JpaTableInputRowData[];
